<template>
  <modal
    name="drive-preview"
    height="auto"
    width="70%"
    :scrollable="true"
    :adaptive="true"
    class="modal-dialog"
    @closed="$emit('close')"
  >
    <div class="modal-content o-common-modal-content">
      <div class="modal-header text-center">
        <button
          type="button"
          class="close"
          @click="closeModal"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <h4 class="modal-title">{{ file.name }}</h4>
      </div>
      <div class="modal-body" v-loading="previewLoading">
        <!-- Image preview -->
        <div v-if="isImage && blobUrl" class="text-center">
          <img
            :src="blobUrl"
            :alt="file.name"
            style="max-width: 100%; max-height: 500px;"
            @load="previewLoading = false"
          />
        </div>

        <!-- PDF / Office doc preview (Office docs converted to PDF server-side) -->
        <div v-else-if="(isPdf || isOfficeDoc) && blobUrl">
          <iframe
            :src="blobUrl"
            style="width: 100%; height: 500px; border: none;"
            @load="previewLoading = false"
          ></iframe>
        </div>

        <!-- Audio preview -->
        <div v-else-if="isAudio && blobUrl" class="text-center" style="padding: 40px;">
          <i class="fa fa-file-audio-o" aria-hidden="true" style="font-size: 64px; color: #F4B400; display: block; margin-bottom: 20px;"></i>
          <audio
            :src="blobUrl"
            controls
            style="width: 100%; max-width: 500px;"
            @loadeddata="previewLoading = false"
          ></audio>
        </div>

        <!-- Video preview -->
        <div v-else-if="isVideo && blobUrl" class="text-center">
          <video
            :src="blobUrl"
            controls
            style="width: 100%; max-height: 500px;"
            @loadeddata="previewLoading = false"
          ></video>
        </div>

        <!-- HTML preview (by extension) -->
        <div v-else-if="isHtmlFile && blobUrl">
          <iframe
            :src="blobUrl"
            style="width: 100%; height: 500px; border: none;"
            @load="previewLoading = false"
          ></iframe>
        </div>

        <!-- Text preview (render as HTML in iframe if content looks like HTML) -->
        <div v-else-if="isText && blobUrl">
          <iframe
            :src="blobUrl"
            style="width: 100%; height: 500px; border: none;"
            @load="previewLoading = false"
          ></iframe>
        </div>

        <!-- Non-previewable file -->
        <div v-else-if="!previewLoading" class="text-center" style="padding: 40px;">
          <i :class="fileIcon" aria-hidden="true" style="font-size: 64px; color: #ccc;"></i>
          <p style="margin-top: 16px; color: #999;">
            Preview is not available for this file type.
          </p>
          <p style="color: #666;">
            <strong>Type:</strong> {{ file.mimeType }}<br />
            <strong>Size:</strong> {{ formatSize(file.size) }}
          </p>
        </div>

        <!-- Deadline info -->
        <div v-if="file.deadline" class="drive-preview-deadline" :style="{ borderLeftColor: deadlineColor }">
          <div class="drive-preview-deadline-header">
            <i class="fa fa-calendar-times-o" :style="{ color: deadlineColor }"></i>
            <strong>Deadline: {{ file.deadline.date }}</strong>
          </div>
          <div v-if="file.deadline.label" style="margin-top: 4px; color: #666;">{{ file.deadline.label }}</div>
          <div style="margin-top: 4px;" :style="{ color: deadlineColor }">
            <template v-if="file.deadline.daysRemaining <= 0">Due today!</template>
            <template v-else>{{ file.deadline.daysRemaining }} day{{ file.deadline.daysRemaining > 1 ? 's' : '' }} remaining</template>
          </div>
        </div>

        <!-- Error state -->
        <div v-if="previewError" class="text-center" style="padding: 20px; color: #c0392b;">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
          Failed to load preview.
        </div>
      </div>
      <div class="modal-footer text-center">
        <a
          v-if="isAdmin && !file.isFolder"
          href="javascript: void(0)"
          @click="$emit('set-deadline', file)"
          class="c-solid-btn"
          :style="{ borderColor: file.deadline ? deadlineColor : '' }"
        >
          <i class="fa fa-calendar-times-o" aria-hidden="true"></i>
          {{ file.deadline ? 'Edit Deadline' : 'Set Deadline' }}
        </a>
        <a
          v-if="file.webViewLink && isNativeGoogleDoc"
          :href="file.webViewLink"
          target="_blank"
          rel="noopener noreferrer"
          class="c-solid-btn c-solid-btn--green"
        >
          <i class="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
        </a>
        <a
          v-if="!file.downloadRestricted || isAdmin"
          href="javascript: void(0)"
          @click="$emit('download', file)"
          class="c-solid-btn c-solid-btn--green"
          style="margin-left: 10px;"
        >
          <i class="fa fa-cloud-download" aria-hidden="true"></i> Download
        </a>
        <a
          href="javascript: void(0)"
          @click="closeModal"
          class="c-solid-btn"
          style="margin-left: 10px;"
        >
          Close
        </a>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import { getApiBase, safeSetXhrHeaders } from "../../global.js";

export default {
  name: "DrivePreviewPanel",
  props: {
    file: { type: Object, required: true },
    isAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      previewLoading: true,
      previewError: false,
      blobUrl: null,
      textContent: null,
    };
  },
  computed: {
    ...mapGetters({
      userToken: "userToken",
      userRole: "userRole",
      // #1470 — canonical (top-level users.role_id), used only on the
      // cs3-context-role header so SAs identify as SA regardless of the
      // per-tenant role they're assuming.
      userCanonicalRole: "userCanonicalRole",
      userClientID: "userClientID",
      employeeId: "employeeId",
    }),
    isNativeGoogleDoc: function () {
      var mime = (this.file.mimeType || "");
      return mime.indexOf("application/vnd.google-apps.") === 0;
    },
    isImage: function () {
      return (this.file.mimeType || "").indexOf("image/") === 0;
    },
    isPdf: function () {
      var mime = (this.file.mimeType || "").toLowerCase();
      return mime === "application/pdf" ||
        mime === "application/vnd.google-apps.document" ||
        mime === "application/vnd.google-apps.presentation" ||
        mime === "application/vnd.google-apps.drawing";
    },
    isAudio: function () {
      return (this.file.mimeType || "").indexOf("audio/") === 0;
    },
    isVideo: function () {
      return (this.file.mimeType || "").indexOf("video/") === 0;
    },
    isOfficeDoc: function () {
      var mime = (this.file.mimeType || "").toLowerCase();
      var officeTypes = [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-powerpoint",
      ];
      return officeTypes.indexOf(mime) !== -1;
    },
    isHtmlFile: function () {
      var ext = (this.file.fileExtension || (this.file.name || "").split(".").pop() || "").toLowerCase();
      return ext === "html" || ext === "htm" || (this.file.mimeType || "").toLowerCase() === "text/html";
    },
    isText: function () {
      if (this.isHtmlFile) return false;
      var mime = (this.file.mimeType || "").toLowerCase();
      if (mime.indexOf("text/") === 0) return true;
      if (mime === "application/json" || mime === "application/xml" || mime === "application/javascript") return true;
      var ext = (this.file.fileExtension || (this.file.name || "").split(".").pop() || "").toLowerCase();
      return ["txt", "md", "log", "json", "csv", "xml", "css", "js"].indexOf(ext) !== -1;
    },
    isPreviewable: function () {
      return this.isImage || this.isPdf || this.isAudio || this.isVideo || this.isOfficeDoc || this.isHtmlFile || this.isText;
    },
    deadlineColor: function () {
      if (!this.file.deadline) return "#ccc";
      var days = this.file.deadline.daysRemaining;
      if (days <= 0) return "#c0392b";
      if (days <= 3) return "#e67e22";
      if (days <= 7) return "#f1c40f";
      return "#27ae60";
    },
    fileIcon: function () {
      var mime = (this.file.mimeType || "").toLowerCase();
      if (mime.indexOf("spreadsheet") !== -1) return "fa fa-file-excel-o";
      if (mime.indexOf("document") !== -1 || mime.indexOf("word") !== -1) return "fa fa-file-word-o";
      if (mime.indexOf("presentation") !== -1) return "fa fa-file-powerpoint-o";
      if (mime.indexOf("video") !== -1) return "fa fa-file-video-o";
      if (mime.indexOf("audio") !== -1) return "fa fa-file-audio-o";
      return "fa fa-file-o";
    },
  },
  mounted() {
    this.$modal.show("drive-preview");
    if (this.isPreviewable) {
      this.loadPreview();
    } else {
      this.previewLoading = false;
    }
  },
  beforeDestroy() {
    if (this.blobUrl) {
      window.URL.revokeObjectURL(this.blobUrl);
    }
  },
  methods: {
    loadPreview: function () {
      var self = this;
      var baseUrl = getApiBase();
      var previewUrl = baseUrl + "drive/files/" + encodeURIComponent(this.file.id) + "/preview";
      var downloadUrl = baseUrl + "drive/files/" + encodeURIComponent(this.file.id) + "/download";

      // Text files: fetch via download endpoint, render in iframe
      if (self.isText) {
        var xhr2 = new XMLHttpRequest();
        xhr2.open("GET", downloadUrl, true);
        xhr2.responseType = "blob";
        safeSetXhrHeaders(xhr2, {
          "Authorization": this.userToken,
          "cs3-context-role": this.userCanonicalRole,
          "cs3-context-clientId": this.userClientID,
          "cs3-context-employeeId": this.employeeId,
        });
        xhr2.onload = function () {
          if (xhr2.status === 200) {
            // Re-wrap as text/html so HTML content renders in iframe
            self.blobUrl = window.URL.createObjectURL(new Blob([xhr2.response], { type: "text/html" }));
          } else {
            self.previewError = true;
            self.previewLoading = false;
          }
        };
        xhr2.onerror = function () { self.previewError = true; self.previewLoading = false; };
        xhr2.send();
        return;
      }

      // HTML files: fetch via download endpoint as blob, re-wrap with correct type for iframe
      if (self.isHtmlFile) {
        var xhrH = new XMLHttpRequest();
        xhrH.open("GET", downloadUrl, true);
        xhrH.responseType = "blob";
        safeSetXhrHeaders(xhrH, {
          "Authorization": this.userToken,
          "cs3-context-role": this.userCanonicalRole,
          "cs3-context-clientId": this.userClientID,
          "cs3-context-employeeId": this.employeeId,
        });
        xhrH.onload = function () {
          if (xhrH.status === 200) {
            self.blobUrl = window.URL.createObjectURL(new Blob([xhrH.response], { type: "text/html" }));
          } else {
            self.previewError = true;
            self.previewLoading = false;
          }
        };
        xhrH.onerror = function () { self.previewError = true; self.previewLoading = false; };
        xhrH.send();
        return;
      }

      var xhr = new XMLHttpRequest();
      xhr.open("GET", previewUrl, true);
      xhr.responseType = "blob";
      safeSetXhrHeaders(xhr, {
        "Authorization": this.userToken,
        "cs3-context-role": this.userCanonicalRole,
        "cs3-context-clientId": this.userClientID,
        "cs3-context-employeeId": this.employeeId,
      });

      xhr.onload = function () {
        if (xhr.status === 200) {
          self.blobUrl = window.URL.createObjectURL(xhr.response);
        } else {
          self.previewError = true;
          self.previewLoading = false;
        }
      };

      xhr.onerror = function () {
        self.previewError = true;
        self.previewLoading = false;
      };

      xhr.send();
    },

    closeModal: function () {
      this.$modal.hide("drive-preview");
      this.$emit("close");
    },

    formatSize: function (bytes) {
      if (!bytes || bytes === 0) return "--";
      var units = ["B", "KB", "MB", "GB"];
      var i = 0;
      var size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return size.toFixed(i > 0 ? 1 : 0) + " " + units[i];
    },
  },
};
</script>

<style scoped>
.modal-title {
  word-break: break-all;
}
.drive-preview-deadline {
  margin: 15px 0;
  padding: 10px 15px;
  background: #fafafa;
  border-left: 4px solid #ccc;
  border-radius: 4px;
}
.drive-preview-deadline-header {
  font-size: 14px;
}
.drive-preview-deadline-header i {
  margin-right: 6px;
}
.drive-preview-text {
  background: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  max-height: 500px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 13px;
}
</style>
